import { useCallback } from "react";
import { Descope } from "@descope/react-sdk";
import { useTheme } from "@/lib/theme/use-theme";
import { Plus, Shield, HelpCircle } from "lucide-react";
import { HarmonyLogoColoredIcon, HarmonyLogoIcon } from "@/components/icons/harmony-logo-icon";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

export function LoginScreen() {
  const { theme } = useTheme();

  const onSuccess = useCallback(() => {
    console.log("Login successful!");
    // Router will handle navigation automatically via route protection
  }, []);

  const onError = useCallback(
    (
      e: CustomEvent<{
        errorCode: string;
        errorDescription: string;
        errorMessage?: string;
        retryAfter?: string;
      }>,
    ) => {
      console.error("Login error:", e.detail);
    },
    [],
  );

  const onReady = useCallback(() => {
    console.log("Descope login form ready");
  }, []);

  return (
    <div className="bg-background flex min-h-screen">
      {/* Left side - Login form */}
      <div className="flex w-1/2 flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-lg lg:w-[32rem]">
          <div className="mb-8 text-center">
            <div className="mb-4 flex justify-center">
              <HarmonyLogoColoredIcon className="h-12 w-12" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              Harmony Management
            </h2>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-center">Sign In</CardTitle>
              <CardDescription className="text-center">
                Use your Harmony workspace account to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Descope
                flowId={
                  window.location.hostname === "localhost"
                    ? "sign-in"
                    : "sign-in-admin"
                }
                onSuccess={onSuccess}
                onError={onError}
                onReady={onReady}
                theme={theme === "dark" ? "dark" : "light"}
                tenant={import.meta.env.VITE_HARMONY_TENANT_ID}
                redirectUrl={
                  window.location.hostname === "localhost"
                    ? `${window.location.origin}/login`
                    : undefined
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="relative w-1/2">
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-purple-600 to-purple-800">
          <div className="flex h-full items-center justify-center p-8">
            <div className="max-w-md text-center text-white">
              <div className="mb-8">
                <HarmonyLogoIcon className="mx-auto mb-6 h-16 w-16 opacity-90 text-white" />
                <h1 className="mb-2 text-4xl font-bold">Harmony Management</h1>
                <p className="text-xl opacity-90">Admin Portal for Harmony</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4 text-left">
                  <Plus className="h-6 w-6 opacity-80" />
                  <div>
                    <div className="font-semibold">Tenant Creation</div>
                    <div className="text-sm opacity-75">
                      Create and manage new tenant environments
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-left">
                  <Shield className="h-6 w-6 opacity-80" />
                  <div>
                    <div className="font-semibold">
                      SSO Setup Configurations
                    </div>
                    <div className="text-sm opacity-75">
                      Configure authentication and identity providers
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-left">
                  <HelpCircle className="h-6 w-6 opacity-80" />
                  <div>
                    <div className="font-semibold">Support Features (WIP)</div>
                    <div className="text-sm opacity-75">
                      Enhanced support tools coming soon
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
